'use client'

import { css, keyframes } from 'styled-components'

const timelineName = '--scroll-shadow'
const fadeDistance = '24px'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`

const shadowAnimation = css`
  animation-fill-mode: both;
  animation-timeline: ${timelineName};
  animation-timing-function: linear;
`

export const scrollShadowSource = css`
  scroll-timeline: ${timelineName} inline;
`

export const scrollShadowScope = css`
  timeline-scope: ${timelineName};
`

export const scrollShadowAtStart = css`
  opacity: 0;

  @supports (animation-timeline: scroll()) {
    ${shadowAnimation};
    animation-name: ${fadeIn};
    animation-range: 0px ${fadeDistance};
  }
`

/** Apply to the shadow at the inline end edge — visible until the region is scrolled to the end. */
export const scrollShadowAtEnd = css`
  opacity: 0;

  @supports (animation-timeline: scroll()) {
    ${shadowAnimation};
    animation-name: ${fadeOut};
    animation-range: calc(100% - ${fadeDistance}) 100%;
  }
`

const edgeGradient = (direction: 'to left' | 'to right', color: string) => css`
  background: linear-gradient(${direction}, ${color} 0%, transparent 100%);
`

/**
 * Fades whichever edge the off-screen content sits behind, so the overflow is discoverable. Apply to
 * the element wrapping the scroller, which must itself carry `scrollShadowSource`.
 */
export const scrollShadowEdges = ({ color, width }: { color: string; width: string }) => css`
  ${scrollShadowScope};
  position: relative;

  &::before,
  &::after {
    bottom: 0;
    content: '';
    pointer-events: none;
    position: absolute;
    top: 0;
    width: ${width};
  }

  &::before {
    ${edgeGradient('to right', color)};
    ${scrollShadowAtStart};
    inset-inline-start: 0;
  }

  &::after {
    ${edgeGradient('to left', color)};
    ${scrollShadowAtEnd};
    inset-inline-end: 0;
  }

  /* Gradients take physical directions, so they flip with the writing mode. */
  .rtl & {
    &::before {
      ${edgeGradient('to left', color)};
    }

    &::after {
      ${edgeGradient('to right', color)};
    }
  }
`
